// VENDORED from @vibgrate/core-open (packages/vibgrate-core-open) by
// scripts/vendor-core-open.mjs. Do not edit here — change the source package
// and re-run the vendor script. Apache-2.0.
import * as semver from 'semver';
import type { Semaphore } from '../utils/semaphore.js';
import {
  getManifestEntry,
  type ManifestAdvisory,
  type ManifestEcosystem,
  type PackageVersionManifest,
} from '../package-version-manifest.js';
import { cvssV3BaseScore, normalizeSeverityLabel, severityFromCvss, severityRank } from '../scoring/cvss.js';
import type {
  AffectedRange,
  Finding,
  PackageVulnerabilities,
  ProjectScan,
  ProjectType,
  VulnEcosystem,
  VulnerabilityAdvisory,
  VulnerabilityScanResult,
  VulnSeverity,
} from '../types.js';

/**
 * Open vulnerability detection.
 *
 * Maps installed packages to known advisories using the public OSV database
 * (online, opt-in via `--vulns`) or advisories carried in an offline
 * package-version manifest (air-gapped). The version-matching and advisory
 * parsing are pure functions; the OSV HTTP calls are a thin, best-effort layer
 * that degrades to "no data" on any failure — a missing result means "not
 * scanned / unreachable", never "no vulnerabilities".
 */

/** A package@version to check for advisories. */
export interface VulnTarget {
  ecosystem: VulnEcosystem;
  package: string;
  version: string;
}

const OSV_BATCH_URL = 'https://api.osv.dev/v1/querybatch';
const OSV_VULN_URL = 'https://api.osv.dev/v1/vulns/';
const OSV_TIMEOUT_MS = 15_000;
const OSV_BATCH_SIZE = 500;
/** Extra attempts after the first for a transient OSV failure (network, abort, 429/5xx). */
const OSV_MAX_RETRIES = 3;
/** Base backoff before the first retry; doubles each attempt (250ms, 500ms, 1s). */
const OSV_RETRY_BASE_MS = 250;
/** HTTP statuses worth retrying — rate-limiting and transient server errors. A 4xx
 *  like 404 is a definitive answer (e.g. an unknown advisory id) and is not retried. */
const OSV_RETRYABLE_STATUS = new Set([408, 425, 429, 500, 502, 503, 504]);

/** Map a scanned project's type to the ecosystem OSV/our manifest understands. */
export function projectTypeToVulnEcosystem(type: ProjectType): VulnEcosystem | null {
  switch (type) {
    case 'node':
    case 'typescript':
      return 'npm';
    case 'python':
      return 'pypi';
    case 'java':
      return 'maven';
    case 'dotnet':
      return 'nuget';
    case 'go':
      return 'go';
    case 'rust':
      return 'cargo';
    case 'php':
      return 'composer';
    case 'ruby':
      return 'rubygems';
    case 'dart':
      return 'pub';
    case 'elixir':
      return 'hex';
    default:
      return null;
  }
}

/** Our ecosystem id → the OSV API's ecosystem string. */
function osvEcosystem(ecosystem: VulnEcosystem): string {
  switch (ecosystem) {
    case 'npm':
      return 'npm';
    case 'pypi':
      return 'PyPI';
    case 'maven':
      return 'Maven';
    case 'nuget':
      return 'NuGet';
    case 'go':
      return 'Go';
    case 'cargo':
      return 'crates.io';
    case 'composer':
      return 'Packagist';
    case 'rubygems':
      return 'RubyGems';
    case 'pub':
      return 'Pub';
    case 'hex':
      return 'Hex';
  }
}

/** Our ecosystem id → the manifest table key (they line up 1:1). */
function manifestEcosystem(ecosystem: VulnEcosystem): ManifestEcosystem {
  return ecosystem as ManifestEcosystem;
}

function emptySeverityCounts(): Record<VulnSeverity, number> {
  return { low: 0, moderate: 0, high: 0, critical: 0, unknown: 0 };
}

function targetKey(t: VulnTarget): string {
  return `${t.ecosystem}\0${t.package}\0${t.version}`;
}

/** Build the deduplicated, deterministically-ordered target list from scanned projects. */
export function collectVulnTargets(projects: ProjectScan[]): VulnTarget[] {
  const seen = new Set<string>();
  const targets: VulnTarget[] = [];
  for (const project of projects) {
    const ecosystem = projectTypeToVulnEcosystem(project.type);
    if (!ecosystem) continue;
    for (const dep of project.dependencies) {
      if (!dep.resolvedVersion) continue;
      const t: VulnTarget = { ecosystem, package: dep.package, version: dep.resolvedVersion };
      const key = targetKey(t);
      if (seen.has(key)) continue;
      seen.add(key);
      targets.push(t);
    }
  }
  targets.sort(
    (a, b) =>
      a.ecosystem.localeCompare(b.ecosystem) ||
      a.package.localeCompare(b.package) ||
      a.version.localeCompare(b.version),
  );
  return targets;
}

/**
 * Whether `version` falls within any affected range or explicit version list.
 * A range is `[introduced, fixed)`; an absent `introduced` means "from the
 * beginning" and an absent `fixed` means "still affected". Versions that aren't
 * valid semver only match the explicit `versions` list.
 */
export function isVersionAffected(
  version: string,
  ranges: Array<{ introduced?: string; fixed?: string }> | undefined,
  versions: string[] | undefined,
): boolean {
  if (versions && versions.includes(version)) return true;
  const v = semver.valid(semver.coerce(version));
  if (!v) return false;
  for (const range of ranges ?? []) {
    const introduced = range.introduced && range.introduced !== '0' ? semver.valid(semver.coerce(range.introduced)) : '0.0.0';
    const fixed = range.fixed ? semver.valid(semver.coerce(range.fixed)) : null;
    const afterIntroduced = introduced ? semver.gte(v, introduced) : true;
    const beforeFixed = fixed ? semver.lt(v, fixed) : true;
    if (afterIntroduced && beforeFixed) return true;
  }
  return false;
}

// ── OSV advisory parsing (pure) ──────────────────────────────────────────────

interface RawOsvSeverity {
  type?: string;
  score?: string;
}
interface RawOsvRangeEvent {
  introduced?: string;
  fixed?: string;
  last_affected?: string;
}
interface RawOsvAffected {
  package?: { ecosystem?: string; name?: string };
  ranges?: Array<{ type?: string; events?: RawOsvRangeEvent[] }>;
  versions?: string[];
  ecosystem_specific?: { severity?: string };
  database_specific?: { severity?: string };
}
interface RawOsvVuln {
  id?: string;
  aliases?: string[];
  summary?: string;
  published?: string;
  withdrawn?: string;
  severity?: RawOsvSeverity[];
  affected?: RawOsvAffected[];
  references?: Array<{ url?: string }>;
  database_specific?: { severity?: string };
}

/** Parse a raw OSV advisory into our shape, scoped to a specific package name. */
export function parseOsvAdvisory(raw: RawOsvVuln, packageName: string): VulnerabilityAdvisory {
  const cvssVector = raw.severity?.find((s) => (s.type ?? '').toUpperCase().startsWith('CVSS_V3'))?.score ?? null;
  const cvss = cvssV3BaseScore(cvssVector);

  // Qualitative fallback severity (GHSA): top-level, then the matching affected entry.
  const lowerName = packageName.toLowerCase();
  const matchingAffected = (raw.affected ?? []).filter((a) => (a.package?.name ?? '').toLowerCase() === lowerName);
  const qualitative =
    raw.database_specific?.severity ??
    matchingAffected.map((a) => a.ecosystem_specific?.severity ?? a.database_specific?.severity).find(Boolean) ??
    null;
  const severity: VulnSeverity = cvss != null ? severityFromCvss(cvss) : normalizeSeverityLabel(qualitative);

  const fixedVersions: string[] = [];
  const affectedRanges: AffectedRange[] = [];
  const affectedVersions: string[] = [];
  for (const affected of matchingAffected) {
    for (const range of affected.ranges ?? []) {
      // OSV events are an ordered sequence of introduced/fixed boundaries.
      let introduced: string | undefined;
      let pending = false;
      for (const event of range.events ?? []) {
        if (event.introduced !== undefined) {
          introduced = event.introduced === '0' ? undefined : event.introduced;
          pending = true;
        }
        if (event.fixed) {
          if (!fixedVersions.includes(event.fixed)) fixedVersions.push(event.fixed);
          affectedRanges.push({ introduced, fixed: event.fixed });
          pending = false;
          introduced = undefined;
        }
      }
      if (pending) affectedRanges.push({ introduced }); // open-ended range (no fix yet)
    }
    for (const v of affected.versions ?? []) if (!affectedVersions.includes(v)) affectedVersions.push(v);
  }

  return {
    id: raw.id ?? 'UNKNOWN',
    aliases: Array.isArray(raw.aliases) ? raw.aliases : [],
    summary: raw.summary ?? null,
    severity,
    cvss,
    cvssVector,
    fixedVersions,
    published: raw.published ?? null,
    withdrawn: raw.withdrawn ?? null,
    references: (raw.references ?? []).map((r) => r.url).filter((u): u is string => Boolean(u)),
    ...(affectedRanges.length ? { affectedRanges } : {}),
    ...(affectedVersions.length ? { affectedVersions } : {}),
  };
}

export function manifestAdvisoryToAdvisory(m: ManifestAdvisory): VulnerabilityAdvisory {
  const cvss = typeof m.cvss === 'number' ? m.cvss : cvssV3BaseScore(m.cvssVector ?? null);
  const severity: VulnSeverity = m.severity
    ? normalizeSeverityLabel(m.severity)
    : cvss != null
      ? severityFromCvss(cvss)
      : 'unknown';
  return {
    id: m.id,
    aliases: m.aliases ?? [],
    summary: m.summary ?? null,
    severity,
    cvss,
    cvssVector: m.cvssVector ?? null,
    fixedVersions: (m.ranges ?? []).map((r) => r.fixed).filter((f): f is string => Boolean(f)),
    published: m.published ?? null,
    withdrawn: m.withdrawn ?? null,
    references: m.references ?? [],
    ...(m.ranges?.length ? { affectedRanges: m.ranges } : {}),
    ...(m.versions?.length ? { affectedVersions: m.versions } : {}),
  };
}

// ── OSV HTTP (thin, best-effort) ─────────────────────────────────────────────

/**
 * Outcome of a best-effort OSV fetch. `ok: false` is a *reachability* failure —
 * the network was down, the request timed out, or OSV returned a transient/4xx
 * error after retries — and is deliberately distinct from a successful empty
 * answer (which arrives as `ok: true` with empty data). Callers use this to tell
 * "OSV said clean" from "OSV could not be asked" (GUARDRAILS §1.4).
 */
type FetchOutcome = { ok: true; data: unknown } | { ok: false };

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchJson(url: string, init: RequestInit, sem: Semaphore): Promise<FetchOutcome> {
  return sem.run(async () => {
    for (let attempt = 0; ; attempt++) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), OSV_TIMEOUT_MS);
      try {
        const res = await fetch(url, { ...init, signal: controller.signal });
        if (res.ok) return { ok: true, data: await res.json() };
        // Non-retryable status (e.g. 404 = unknown id) is a definitive answer, not
        // a reachability failure to retry — give up immediately.
        if (!OSV_RETRYABLE_STATUS.has(res.status)) return { ok: false };
      } catch {
        // Network error / timeout (abort) — fall through to the retry decision.
      } finally {
        clearTimeout(timer);
      }
      if (attempt >= OSV_MAX_RETRIES) return { ok: false };
      // Fixed exponential backoff (no jitter) keeps the engine deterministic.
      await sleep(OSV_RETRY_BASE_MS * 2 ** attempt);
    }
  });
}

/** Per-target advisory ids plus whether every batch was answered (OSV reachable). */
interface OsvBatchResult {
  idsByTarget: Map<string, string[]>;
  /** True only when every batch request succeeded; false if any batch failed after
   *  retries, so an empty `idsByTarget` can be read as "unreachable", not "clean". */
  allBatchesOk: boolean;
}

/** Query OSV for advisory ids affecting each target, in bulk batches. */
async function osvQueryBatch(targets: VulnTarget[], sem: Semaphore): Promise<OsvBatchResult> {
  const idsByTarget = new Map<string, string[]>();
  let allBatchesOk = true;
  for (let i = 0; i < targets.length; i += OSV_BATCH_SIZE) {
    const chunk = targets.slice(i, i + OSV_BATCH_SIZE);
    const body = JSON.stringify({
      queries: chunk.map((t) => ({ version: t.version, package: { name: t.package, ecosystem: osvEcosystem(t.ecosystem) } })),
    });
    const outcome = await fetchJson(OSV_BATCH_URL, { method: 'POST', body, headers: { 'content-type': 'application/json' } }, sem);
    if (!outcome.ok) {
      allBatchesOk = false;
      continue;
    }
    const data = outcome.data as { results?: Array<{ vulns?: Array<{ id?: string }> }> } | null;
    if (!data?.results) {
      // A 200 with an unparseable/missing body is still an answer we can't use.
      allBatchesOk = false;
      continue;
    }
    for (let j = 0; j < chunk.length; j++) {
      const ids = (data.results[j]?.vulns ?? []).map((v) => v.id).filter((id): id is string => Boolean(id));
      if (ids.length) idsByTarget.set(targetKey(chunk[j]), ids);
    }
  }
  return { idsByTarget, allBatchesOk };
}

/** Fetch + parse full advisory details for a set of ids, deduplicated. */
async function osvFetchAdvisories(ids: Set<string>, sem: Semaphore): Promise<Map<string, RawOsvVuln>> {
  const out = new Map<string, RawOsvVuln>();
  await Promise.all(
    [...ids].map(async (id) => {
      const outcome = await fetchJson(`${OSV_VULN_URL}${encodeURIComponent(id)}`, { method: 'GET' }, sem);
      if (outcome.ok && outcome.data) out.set(id, outcome.data as RawOsvVuln);
    }),
  );
  return out;
}

// ── Orchestration ────────────────────────────────────────────────────────────

function finalize(source: VulnerabilityScanResult['source'], packages: PackageVulnerabilities[]): VulnerabilityScanResult {
  // Sort packages and advisories deterministically.
  packages.sort(
    (a, b) =>
      a.ecosystem.localeCompare(b.ecosystem) ||
      a.package.localeCompare(b.package) ||
      a.version.localeCompare(b.version),
  );
  const severityCounts = emptySeverityCounts();
  let totalAdvisories = 0;
  for (const pkg of packages) {
    pkg.advisories.sort((a, b) => severityRank(b.severity) - severityRank(a.severity) || a.id.localeCompare(b.id));
    totalAdvisories += pkg.advisories.length;
    const worst = pkg.advisories.reduce<VulnSeverity>((w, a) => (severityRank(a.severity) > severityRank(w) ? a.severity : w), 'unknown');
    severityCounts[worst]++;
  }
  return { source, packages, totalAdvisories, severityCounts };
}

/** A fresh empty result each call — never a shared object, so downstream
 * attribution can attach `cra` without mutating a module-level constant. */
function emptyResult(): VulnerabilityScanResult {
  return {
    source: 'none',
    packages: [],
    totalAdvisories: 0,
    severityCounts: { low: 0, moderate: 0, high: 0, critical: 0, unknown: 0 },
  };
}

/**
 * Resolve advisories for the given targets.
 *
 * - Online (default): query OSV. Degrades to `source: 'none'` if OSV is unreachable.
 * - Offline: match against advisories carried in the package-version manifest.
 */
export async function scanVulnerabilities(
  targets: VulnTarget[],
  opts: { sem: Semaphore; offline?: boolean; manifest?: PackageVersionManifest },
): Promise<VulnerabilityScanResult> {
  if (targets.length === 0) return emptyResult();

  // Offline / air-gapped: advisories come only from the manifest.
  if (opts.offline) {
    return scanFromManifest(targets, opts.manifest);
  }

  // Online: OSV. (A manifest, if present, augments with private advisories.)
  const { idsByTarget, allBatchesOk } = await osvQueryBatch(targets, opts.sem);
  if (idsByTarget.size === 0) {
    // No OSV matches. Fold in any manifest advisories first; if there are none,
    // report `osv` (checked & clean) only when every batch was answered. If a
    // batch failed, report `unreachable` so an empty result is never mistaken
    // for "no vulnerabilities" (GUARDRAILS §1.4 — absent ≠ zero).
    const manifestResult = scanFromManifest(targets, opts.manifest);
    if (manifestResult.packages.length) return manifestResult;
    return { ...emptyResult(), source: allBatchesOk ? 'osv' : 'unreachable' };
  }

  const allIds = new Set<string>();
  for (const ids of idsByTarget.values()) for (const id of ids) allIds.add(id);
  const rawById = await osvFetchAdvisories(allIds, opts.sem);

  const manifestByTarget = manifestAdvisoryMap(targets, opts.manifest);
  const packages: PackageVulnerabilities[] = [];
  for (const target of targets) {
    const ids = idsByTarget.get(targetKey(target)) ?? [];
    const advisories: VulnerabilityAdvisory[] = [];
    const seenIds = new Set<string>();
    for (const id of ids) {
      const raw = rawById.get(id);
      if (!raw || raw.withdrawn) continue;
      advisories.push(parseOsvAdvisory(raw, target.package));
      seenIds.add(id);
    }
    for (const adv of manifestByTarget.get(targetKey(target)) ?? []) {
      if (!seenIds.has(adv.id)) advisories.push(adv);
    }
    if (advisories.length) packages.push({ ...target, advisories });
  }
  return finalize('osv', packages);
}

/** Build per-target manifest advisories (matched against the installed version). */
function manifestAdvisoryMap(targets: VulnTarget[], manifest?: PackageVersionManifest): Map<string, VulnerabilityAdvisory[]> {
  const map = new Map<string, VulnerabilityAdvisory[]>();
  if (!manifest) return map;
  for (const target of targets) {
    const entry = getManifestEntry(manifest, manifestEcosystem(target.ecosystem), target.package);
    const matched = (entry?.vulns ?? []).filter(
      (m) => !m.withdrawn && isVersionAffected(target.version, m.ranges, m.versions),
    );
    if (matched.length) map.set(targetKey(target), matched.map(manifestAdvisoryToAdvisory));
  }
  return map;
}

function scanFromManifest(targets: VulnTarget[], manifest?: PackageVersionManifest): VulnerabilityScanResult {
  const byTarget = manifestAdvisoryMap(targets, manifest);
  if (byTarget.size === 0) return emptyResult();
  const packages: PackageVulnerabilities[] = [];
  for (const target of targets) {
    const advisories = byTarget.get(targetKey(target));
    if (advisories?.length) packages.push({ ...target, advisories });
  }
  return finalize('manifest', packages);
}

// ── Findings ─────────────────────────────────────────────────────────────────

/** SARIF/text rule id for vulnerability findings. */
export const VULN_RULE_ID = 'vibgrate/vulnerability';

function levelForSeverity(severity: VulnSeverity): Finding['level'] {
  switch (severity) {
    case 'critical':
    case 'high':
      return 'error';
    case 'moderate':
      return 'warning';
    default:
      return 'note';
  }
}

/** One finding per (package, advisory) so each CVE surfaces as its own code-scanning alert. */
export function generateVulnerabilityFindings(result: VulnerabilityScanResult): Finding[] {
  const findings: Finding[] = [];
  for (const pkg of result.packages) {
    for (const adv of pkg.advisories) {
      const cve = adv.aliases.find((a) => a.startsWith('CVE-'));
      const idLabel = cve && cve !== adv.id ? `${adv.id} (${cve})` : adv.id;
      const fixed = adv.fixedVersions.length ? ` — fixed in ${adv.fixedVersions.join(', ')}` : ' — no fix available';
      const cvssLabel = adv.cvss != null ? ` ${adv.cvss}` : '';
      const attribution =
        adv.introduced != null
          ? ` — introduced by ${adv.introduced.authorName} in ${adv.introduced.shortSha}${adv.exposureDays != null ? ` (${adv.exposureDays}d exposed)` : ''}`
          : '';
      findings.push({
        ruleId: VULN_RULE_ID,
        level: levelForSeverity(adv.severity),
        message: `${pkg.package}@${pkg.version}: ${idLabel} (${adv.severity}${cvssLabel})${fixed}${attribution}`,
        location: pkg.package,
        details: {
          ecosystem: pkg.ecosystem,
          package: pkg.package,
          installedVersion: pkg.version,
          advisoryId: adv.id,
          aliases: adv.aliases,
          severity: adv.severity,
          cvss: adv.cvss,
          fixedVersions: adv.fixedVersions,
          ...(adv.introduced
            ? {
                introducedBy: adv.introduced.authorName,
                introducedSha: adv.introduced.shortSha,
                introducedDate: adv.introduced.date,
                exposureDays: adv.exposureDays,
              }
            : {}),
        },
      });
    }
  }
  return findings;
}
